import React, { useEffect, useState } from "react";
import { CreateRequest, DeleteRequest, UpdateRequest } from "./crud";
import TitledDialog from "../components/TitledDialog";
import { useFetch } from "../components/FetchProvider";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Alert, Box, Button, MenuItem, Select, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { submitBatch } from "./crud";
import { defineMessage, FormattedMessage, MessageDescriptor, useIntl } from "react-intl";
import * as z from "zod";
import { zodResolver } from "./form";
import { useErrorMessages } from "./error-messages";

function useValidationSchema() {
    const {formatError} = useErrorMessages();

    return z
        .object({
            pattern: z.string().min(1, formatError("required_error")),
            role: z.number(),
            context: z.string(),
        })
        .required();
}

function findRule(roles: any[], ruleId: number) {
    for (const role of roles) {
        const rule = role.client_role_mapping_rules.find((rule: any) => rule.id == ruleId);
        if (rule) return rule;
    }
}

function getSubstitutionFromRule(rule: any) {
    return rule.context_substitutions.length > 0
        ? Object.values<string>(rule.context_substitutions[0].substitution)[0]
        : "";
}

const nullDefaultValues = { pattern: "", role: 0, context: "" };
function getDefaultValues(roles: any[], updatingRule: any) {
    if (roles.length === 0) return nullDefaultValues;

    if (updatingRule) {
        return {
            pattern: updatingRule.pattern,
            role: updatingRule.role.id,
            context: getSubstitutionFromRule(updatingRule),
        };
    } else {
        return { pattern: "", role: roles[0].id, context: "" };
    }
}

function findEditableContext(role: any) {
    return role.role_contexts.find((rc: any) => rc.predefined_value === null);
}

type FormInputs = z.infer<ReturnType<typeof useValidationSchema>>;

const RuleForm: React.FC<{
    ruleId?: number;
    onDirtyChange?: (isDirty: boolean) => void;
    roles?: any[];
    onSuccess?: () => void;
}> = ({ ruleId, roles, onDirtyChange, onSuccess }) => {
    const schema = useValidationSchema();
    const fetch = useFetch();
    const isMountedRef = React.useRef(true);
    const [serverError, setServerError] = useState<MessageDescriptor | null>(null);

    /// the rule we are currently updating
    const updatingRule = ruleId ? findRule(roles, ruleId) : null;

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors, isDirty },
    } = useForm<FormInputs>({
        resolver: zodResolver(schema),
        defaultValues: getDefaultValues(roles, updatingRule),
        mode: "onChange",
    });

    const formValues = watch();

    // Report dirty state changes up to parent
    useEffect(() => {
        onDirtyChange?.(isDirty);

        return () => {
            isMountedRef.current = false;
        };
    }, [isDirty, onDirtyChange]);

    const currentRole = roles.find((role) => role.id == formValues.role);
    const editableContext = currentRole ? findEditableContext(currentRole) : null;
    const editableContextLabel = editableContext ? Object.values<any>(editableContext.context.schema)[0].name : null;

    const onSubmit: SubmitHandler<any> = async (data) => {
        try {
            if (!ruleId) {
                const requests = [
                    new CreateRequest("client_role_mapping_rule", { role: data.role, pattern: data.pattern }),
                ];

                if (editableContext) {
                    requests.push(
                        new CreateRequest("client_role_mapping_context_substitution", {
                            client_role_mapping_rule: {
                                gaussReferenceValue: {
                                    index: 0,
                                    target: "data.id",
                                },
                            },
                            context: editableContext.context.id,
                            substitution: {
                                [editableContextLabel]: data.context,
                            },
                        }),
                    );
                }

                await submitBatch(fetch, requests);
                onSuccess();
            } else {
                const requests = [];
                if (data.role != updatingRule.role.id || data.pattern != updatingRule.pattern)
                    requests.push(
                        new UpdateRequest(
                            "client_role_mapping_rule",
                            { role: data.role, pattern: data.pattern },
                            ruleId,
                        ),
                    );

                if (updatingRule.role.id != data.role) {
                    // role changed - remove any current substitutions and create new ones if needed
                    for (const sub of updatingRule.context_substitutions) {
                        requests.push(new DeleteRequest("client_role_mapping_context_substitution", sub.id));
                    }

                    if (editableContext) {
                        requests.push(
                            new CreateRequest("client_role_mapping_context_substitution", {
                                client_role_mapping_rule: ruleId,
                                context: editableContext.context.id,
                                substitution: {
                                    [editableContextLabel]: data.context,
                                },
                            }),
                        );
                    }
                } else {
                    // role is the same - update context value if changed
                    if (editableContext && data.context != getSubstitutionFromRule(updatingRule)) {
                        requests.push(
                            new UpdateRequest(
                                "client_role_mapping_context_substitution",
                                {
                                    client_role_mapping_rule: ruleId,
                                    context: editableContext.context.id,
                                    substitution: {
                                        [editableContextLabel]: data.context,
                                    },
                                },
                                updatingRule.context_substitutions[0].id,
                            ),
                        );
                    }
                }

                await submitBatch(fetch, requests);
                onSuccess();
            }
        } catch (error) {
            setServerError(defineMessage({
                id: "error.add_role_failed",
                defaultMessage: "Failed to add role. Please try again."
            }));
        }
    };

    const hasErrors = false;

    return (
        <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} maxWidth="60ch">
            <Grid container spacing={2} alignItems="center">
                <Grid size={4}>
                    <FormattedMessage id="client_role_mapping_rule.pattern" defaultMessage="Pattern" />
                </Grid>
                <Grid size={8}>
                    <Controller
                        control={control}
                        name="pattern"
                        render={({ field }) => (
                            <TextField
                                required
                                {...field}
                                size="small"
                                fullWidth
                                error={!!errors.pattern}
                                helperText={errors.pattern ? errors.pattern.message : null}
                            />
                        )}
                    />
                </Grid>

                <Grid size={4}>
                    <FormattedMessage id="client_role_mapping_rule.role" defaultMessage="$user_roles.role" />
                </Grid>
                <Grid size={8}>
                    <Controller
                        control={control}
                        name="role"
                        render={({ field }) => (
                            <Select {...field} size="small" fullWidth>
                                {Object.values(roles).map((role) => {
                                    return (
                                        <MenuItem key={role.id} value={role.id}>
                                            {role.name}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        )}
                    />
                </Grid>

                <Grid size={4} sx={{ display: editableContext ? "block" : "none" }}>
                    {/* eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id */}
                    {editableContext && <FormattedMessage id={editableContextLabel} />}
                </Grid>
                <Grid size={8} sx={{ display: editableContext ? "block" : "none" }}>
                    <Controller
                        control={control}
                        name="context"
                        render={({ field }) => <TextField {...field} size="small" fullWidth />}
                    />
                </Grid>

                {serverError && (
                    <Grid size={12} justifyContent="flex-end">
                        <Alert severity="error">
                            <FormattedMessage {...serverError} />
                        </Alert>
                    </Grid>
                )}
                <Grid size={12} container justifyContent="flex-end" spacing={1}>
                    <Grid>
                        <Button type="submit" disabled={hasErrors || !isDirty}>
                            <FormattedMessage id="form.save" defaultMessage="Save" />
                        </Button>
                    </Grid>
                    <Grid>
                        <Button onClick={() => reset()} disabled={!isDirty}>
                            <FormattedMessage id="form.reset" defaultMessage="Reset" />
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

const ClientRoleMappingRuleDialog: React.FC<{
    open: boolean;
    ruleId?: number;
    roles: any[];
    onHide: (changed: boolean) => void;
}> = ({ ruleId, open, onHide, roles }) => {
    const [isFormDirty, setIsFormDirty] = React.useState(false);

    return (
        <TitledDialog
            open={open}
            hide={() => onHide(false)}
            isContentChanged={() => isFormDirty}
            title="client_role_mapping_rule.create"
        >
            <RuleForm ruleId={ruleId} roles={roles} onSuccess={() => onHide(true)} onDirtyChange={setIsFormDirty} />
        </TitledDialog>
    );
};

export default ClientRoleMappingRuleDialog;
